import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, LayoutGrid, List as ListIcon, Map as MapIcon, SlidersHorizontal, X, Star, Zap } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import ServiceCard from "@/components/marketplace/ServiceCard";
import MapView from "@/components/marketplace/MapView";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const KIND_TABS = [
  { key: "", label: "All" },
  { key: "local", label: "Local" },
  { key: "digital", label: "Digital" },
];
const SORTS = [
  { k: "recommended", l: "Recommended" },
  { k: "highest_rated", l: "Highest rated" },
  { k: "most_reviewed", l: "Most reviewed" },
  { k: "lowest_price", l: "Lowest price" },
  { k: "highest_price", l: "Highest price" },
  { k: "fastest_delivery", l: "Fastest delivery" },
  { k: "most_completed", l: "Most jobs" },
  { k: "recently_added", l: "Newest" },
];
const VIEWS = [
  { key: "grid", label: "Grid", icon: LayoutGrid },
  { key: "list", label: "List", icon: ListIcon },
  { key: "map", label: "Map", icon: MapIcon },
];

export default function ServiceDirectoryPage({
  headline = "Find your Gulf Coast service.",
  eyebrow = "Services",
  lockKind = null,
}) {
  useDocumentTitle(lockKind === "local" ? "Book local pros" : lockKind === "digital" ? "Hire digital talent" : "Services");
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rootCategories, setRootCategories] = useState([]);
  const [states, setStates] = useState([]);

  const q = sp.get("q") || "";
  const kindParam = sp.get("kind") || "";
  const kind = lockKind || kindParam;
  const category_id = sp.get("category_id") || "";
  const min_price = sp.get("min_price") || "";
  const max_price = sp.get("max_price") || "";
  const min_rating = sp.get("min_rating") || "";
  const instant = sp.get("instant") === "1";
  const same_day = sp.get("same_day") === "1";
  const featured = sp.get("featured") === "1";
  const sort = sp.get("sort") || "recommended";
  const view = sp.get("view") || "grid";
  const service_type = sp.get("service_type") || "";

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([
        api.get("/categories", { params: { include_disabled: false } }),
        api.get("/locations/states"),
      ]);
      setRootCategories(c.data.filter((x) => x.depth === 0));
      setStates(s.data);
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const params = { sort, limit: 24 };
        if (q) params.q = q;
        if (kind) params.kind = kind;
        if (category_id) params.category_id = category_id;
        if (min_price) params.min_price = min_price;
        if (max_price) params.max_price = max_price;
        if (min_rating) params.min_rating = min_rating;
        if (instant) params.instant_only = true;
        if (same_day) params.same_day_only = true;
        if (featured) params.featured_only = true;
        if (service_type) params.service_type = service_type;
        const { data } = await api.get("/services", { params });
        setItems(data.items || []);
        setTotal(data.total || 0);
      } finally {
        setLoading(false);
      }
    })();
  }, [q, kind, category_id, min_price, max_price, min_rating, instant, same_day, featured, sort, service_type]);

  const upd = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined || v === false) next.delete(k);
      else next.set(k, v === true ? "1" : v);
    });
    setSp(next, { replace: true });
  };

  const activeFilters = useMemo(() => {
    const active = [];
    if (category_id) {
      const c = rootCategories.find((r) => r.id === category_id);
      if (c) active.push({ label: c.name, clear: () => upd({ category_id: "" }) });
    }
    if (min_price) active.push({ label: `≥ $${min_price}`, clear: () => upd({ min_price: "" }) });
    if (max_price) active.push({ label: `≤ $${max_price}`, clear: () => upd({ max_price: "" }) });
    if (min_rating) active.push({ label: `★ ≥ ${min_rating}`, clear: () => upd({ min_rating: "" }) });
    if (instant) active.push({ label: "Instant book", clear: () => upd({ instant: false }) });
    if (same_day) active.push({ label: "Same-day", clear: () => upd({ same_day: false }) });
    if (featured) active.push({ label: "Featured", clear: () => upd({ featured: false }) });
    if (service_type) active.push({ label: service_type, clear: () => upd({ service_type: "" }) });
    return active;
  }, [category_id, min_price, max_price, min_rating, instant, same_day, featured, service_type, rootCategories]);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{eyebrow}</div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold text-[#071b22] tracking-tight" data-testid="services-title">
              {headline}
            </h1>
            <p className="mt-3 text-[#252A34]/75">
              {total.toLocaleString()} services across FL, AL, MS, LA & TX.
            </p>
          </div>

          {!lockKind && (
            <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1" role="tablist" aria-label="Service kind">
              {KIND_TABS.map((t) => (
                <button
                  key={t.key || "all"}
                  onClick={() => upd({ kind: t.key })}
                  data-testid={`kind-tab-${t.key || "all"}`}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    (t.key || "") === (kind || "") ? "bg-white text-[#071b22] shadow-sm" : "text-[#252A34]/70"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Search + top filter row */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 mb-6 flex flex-col md:flex-row md:items-center gap-3" data-testid="service-filters">
          <label className="flex-1 flex items-center gap-2 rounded-xl border border-slate-200 px-3 focus-within:border-[#165a71]">
            <Search className="h-4 w-4 text-[#165a71]" />
            <input
              type="text"
              value={q}
              onChange={(e) => upd({ q: e.target.value })}
              placeholder="What do you need done?"
              className="flex-1 py-2.5 outline-none bg-transparent text-sm"
              data-testid="services-search"
            />
          </label>

          <select
            value={category_id}
            onChange={(e) => upd({ category_id: e.target.value })}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm min-w-[180px]"
            data-testid="services-category"
          >
            <option value="">Any category</option>
            {rootCategories
              .filter((c) => (kind ? c.kind === kind : true))
              .map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
          </select>

          <select
            value={sort}
            onChange={(e) => upd({ sort: e.target.value })}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
            data-testid="services-sort"
          >
            {SORTS.map((s) => (<option key={s.k} value={s.k}>{s.l}</option>))}
          </select>

          <Sheet>
            <SheetTrigger asChild>
              <button className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-medium hover:border-[#165a71]/40 transition-colors" data-testid="open-filters-drawer">
                <SlidersHorizontal className="h-4 w-4" /> More filters
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[380px] sm:w-[420px] overflow-y-auto" data-testid="filters-drawer">
              <SheetHeader><SheetTitle className="font-display">All filters</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-6">
                <Section title="Price">
                  <div className="flex gap-3">
                    <NumField label="Min $" value={min_price} onChange={(v) => upd({ min_price: v })} data-testid="filter-min-price" />
                    <NumField label="Max $" value={max_price} onChange={(v) => upd({ max_price: v })} data-testid="filter-max-price" />
                  </div>
                </Section>
                <Section title="Rating">
                  <div className="flex flex-wrap gap-2">
                    {[4, 4.5, 4.8].map((r) => (
                      <button
                        key={r}
                        onClick={() => upd({ min_rating: min_rating == r ? "" : r })}
                        data-testid={`filter-rating-${r}`}
                        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium ${
                          min_rating == r ? "bg-[#165a71] text-white border-[#165a71]" : "border-slate-200 text-[#252A34]"
                        }`}
                      >
                        <Star className="h-3.5 w-3.5" /> {r}+
                      </button>
                    ))}
                  </div>
                </Section>
                <Section title="Availability">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={instant} onChange={(e) => upd({ instant: e.target.checked })} data-testid="filter-instant" className="h-4 w-4 rounded border-slate-300 text-[#165a71]" /> Instant booking
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={same_day} onChange={(e) => upd({ same_day: e.target.checked })} data-testid="filter-same-day" className="h-4 w-4 rounded border-slate-300 text-[#165a71]" /> Same-day
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={featured} onChange={(e) => upd({ featured: e.target.checked })} data-testid="filter-featured" className="h-4 w-4 rounded border-slate-300 text-[#165a71]" /> Featured only
                  </label>
                </Section>
                <Section title="Service type">
                  <div className="flex flex-wrap gap-2">
                    {["instant_fixed_local", "hourly_local", "quote_local_project", "digital_package", "custom_digital_project", "consultation", "recurring_service"].map((t) => (
                      <button
                        key={t}
                        onClick={() => upd({ service_type: service_type === t ? "" : t })}
                        data-testid={`filter-service-type-${t}`}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                          service_type === t ? "bg-[#165a71] text-white border-[#165a71]" : "border-slate-200 text-[#252A34]"
                        }`}
                      >
                        {t.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                </Section>
              </div>
            </SheetContent>
          </Sheet>

          {/* view toggle */}
          <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                onClick={() => upd({ view: v.key })}
                data-testid={`view-${v.key}`}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium ${
                  view === v.key ? "bg-white text-[#071b22] shadow-sm" : "text-[#252A34]/70"
                }`}
              >
                <v.icon className="h-3.5 w-3.5" /> {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* active filter chips */}
        {activeFilters.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2" data-testid="active-filters">
            {activeFilters.map((f, i) => (
              <button key={i} onClick={f.clear} className="inline-flex items-center gap-1 rounded-full bg-[#165a71]/10 text-[#165a71] px-3 py-1.5 text-xs font-medium hover:bg-[#165a71]/20 transition-colors">
                {f.label} <X className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-[#252A34]/60" data-testid="services-loading">Loading services…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center bg-white" data-testid="services-empty">
            <div className="font-display text-xl text-[#071b22]">No services match those filters.</div>
            <div className="text-[#252A34]/70 mt-2 text-sm">Try loosening filters or clearing them all.</div>
          </div>
        ) : view === "map" ? (
          <MapView items={items} height={620} />
        ) : view === "list" ? (
          <div className="space-y-3">
            {items.map((s, i) => (
              <div key={s.id} className="rounded-2xl border border-slate-200 bg-white flex overflow-hidden">
                <img src={s.featured_image} alt={s.title} className="h-32 w-40 object-cover" />
                <div className="p-4 flex-1 min-w-0">
                  <div className="font-display font-semibold text-[#071b22]">{s.title}</div>
                  <div className="text-sm text-[#252A34]/70 line-clamp-2 mt-1">{s.short_description}</div>
                  <div className="mt-2 text-xs text-[#252A34]/60">★ {(s.rating_avg || 0).toFixed(1)} · from ${s.base_price}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {items.map((s, i) => (
              <ServiceCard key={s.id} service={s} index={i} sponsored={s.is_featured && featured} />
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="text-xs font-bold uppercase tracking-wider text-[#252A34]/60 mb-3">{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);
const NumField = ({ label, value, onChange, ...props }) => (
  <label className="flex-1 block">
    <span className="text-[11px] uppercase tracking-wider text-[#252A34]/60 font-medium">{label}</span>
    <input
      type="number"
      min="0"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#165a71]"
      {...props}
    />
  </label>
);
